import type { FormProps as ReeferFormProps } from '@iheartjane/reefer-hook-form';
import { Form as ReeferForm } from '@iheartjane/reefer-hook-form';
import type { ReactNode } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { FormProps as RemixFormProps } from 'react-router';

import { FormWithBasename } from '@jane/bloom/components/formWithBasename';

export function ReeferCompatForm<T>({
  children,
  className,
  hidden,
  hiddenValues,
  id,
  initialData,
  intent,
  name,
  onSubmit,
  ...props
}: Omit<ReeferFormProps & RemixFormProps, 'onSubmit' | 'children'> & {
  children?: ReactNode;
  hiddenValues?: Record<string, string | number>;
  initialData?: T;
  intent?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onSubmit?: (formData: any) => void;
}) {
  // default id to name, since id is used when referencing the form
  // in other html elements like button form="id"
  id = id || name;

  const formRef = useRef<HTMLFormElement>(null);
  const [initialRun, setInitialRun] = useState(true);
  const [formData, setFormData] = useState(initialData);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleSubmit = (newFormData: any) => {
    if (!onSubmit) {
      setFormData(newFormData);
    }

    if (onSubmit) {
      onSubmit(newFormData);
    }
  };

  useEffect(() => {
    if (!initialRun && formData) {
      formRef.current?.requestSubmit();
    }

    setInitialRun(false);
  }, [formData, initialRun]);

  const formFields = useMemo(() => {
    const withData = Object.assign({}, hiddenValues, formData);
    return Object.entries(withData);
  }, [formData, hiddenValues]);

  const reeferFormName =
    `reefer` + name.slice(0, 1).toUpperCase() + name.slice(1);

  const reeferFormId = `reefer` + id.slice(0, 1).toUpperCase() + id.slice(1);

  return (
    <>
      {!onSubmit && (
        <FormWithBasename
          ref={formRef}
          intent={intent}
          name={name}
          id={id}
          {...props}
          hidden
        >
          {formFields.map(([key, value]) => (
            <input key={key} type="hidden" name={key} defaultValue={value} />
          ))}
        </FormWithBasename>
      )}
      <div hidden={hidden} className={className}>
        <ReeferForm
          {...props}
          id={reeferFormId}
          name={reeferFormName}
          onSubmit={handleSubmit}
        >
          {children}
        </ReeferForm>
      </div>
    </>
  );
}
